import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["onRef", "activeRowId", "setActiveRowId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useReducer, useCallback, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Loader, Modal, Text, Advice, Link, Icon } from 'hoi-poi-ui';
import { downloadURI } from "../../../lib/helpers";
import { useWeb5Context } from "../../../lib/Web5Context";
import { getErrors, getWarnings } from "../../../lib/services/imports";
import { showErrorToast } from "../../../lib/errors";
import ErrorList from "./ErrorList";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'ImportedModal');
const initialState = {
  data: {},
  isOpen: false,
  // FIXME
  loading: false,
  errors: null,
  excelUrlErrors: null,
  excelUrlWarnings: null,
  showError: false,
  errorData: null
};
function reducer(state, action) {
  switch (action.type) {
    case 'init':
      return _objectSpread(_objectSpread({}, initialState), {}, {
        data: action.data,
        isOpen: true,
        loading: true,
        errors: null,
        excelUrlErrors: null,
        excelUrlWarnings: null
      });
    case 'close':
      return _objectSpread(_objectSpread({}, state), {}, {
        isOpen: false
      });
    case 'setErrors':
      return _objectSpread(_objectSpread({}, state), {}, {
        errors: action.errors,
        excelUrlErrors: action.excelUrlErrors,
        loading: false
      });
    case 'setWarnings':
      return _objectSpread(_objectSpread({}, state), {}, {
        warnings: action.warnings,
        excelUrlWarnings: action.excelUrlWarnings,
        loading: false
      });
    case 'setShowError':
      return _objectSpread(_objectSpread({}, state), {}, {
        showError: action.showError,
        errorData: action.errorData
      });
    default:
      throw new Error('No action provided');
  }
}
var _ref2 = /*#__PURE__*/React.createElement(Loader, {
  size: "big"
});
const ImportedModal = _ref => {
  var _state$errorData3, _state$errorData4;
  let {
      onRef,
      activeRowId,
      setActiveRowId
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const classes = useStyles();
  const {
    t
  } = useWeb5Context();
  const [state, dispatch] = useReducer(reducer, initialState);

  // Modal handlers
  const onClose = useCallback(() => {
    setActiveRowId(null);
    dispatch({
      type: 'close'
    });
  }, [dispatch, setActiveRowId]);
  const onDownload = useCallback(isWarning => {
    if (isWarning) downloadURI(state.excelUrlWarnings);else downloadURI(state.excelUrlErrors);
  }, [state]);
  const fetchData = useCallback((id, isErrors, dispatch, onClose, t) => {
    return new Promise((resolve, reject) => {
      let pr;
      if (isErrors) {
        pr = getErrors(id);
      } else {
        pr = getWarnings(id);
      }
      pr.then(result => {
        const {
          excelUrl
        } = result;
        const data = isErrors ? result.errors : result.warnings;
        if (!Array.isArray(data)) {
          onClose();
          reject('No data available');
        }
        const actionType = isErrors ? 'setErrors' : 'setWarnings';
        const excelUrlKey = isErrors ? 'excelUrlErrors' : 'excelUrlWarnings';
        const key = isErrors ? 'errors' : 'warnings';
        dispatch({
          type: actionType,
          [excelUrlKey]: excelUrl,
          [key]: data.reduce((arr, item) => {
            arr = [...arr, ...item[key].map(e => ({
              row: item.row,
              name: e.name,
              error: e.error,
              value: e.value
            }))];
            return arr;
          }, [])
        });
        resolve();
      }).catch(error => {
        console.error(error);
        onClose();
        showErrorToast(t, error);
        reject(error);
      });
    });
  }, []);

  // Child method
  useEffect(() => {
    onRef && onRef({
      open: data => {
        dispatch({
          type: 'init',
          data
        });
        if (data.resultNOK) {
          fetchData(data.id, true, dispatch, onClose, t);
        }
        if (data.resultWOK) {
          fetchData(data.id, false, dispatch, onClose, t);
        }
      }
    });
  }, [onRef, dispatch, onClose, t, fetchData]);

  // Modal data
  const dataModal = useMemo(() => {
    return state !== null && state !== void 0 && state.loading || state !== null && state !== void 0 && state.showError ? {} : {
      onConfirm: onClose,
      confirmText: t('cfm_label_confirm_ok')
    };
  }, [state, onClose, t]);
  const setErrorMessage = useCallback((showError, errorData) => dispatch({
    type: 'setShowError',
    showError,
    errorData
  }), []);
  const listContentModal = useMemo(() => {
    const okAdvice = !!(state.data && state.data.resultOK);
    const warningLiteral = state.data.resultWOK > 1 ? 'cfm_label_import_warning_numbers' : 'cfm_label_import_warning_number';
    const errorsLiteral = state.data.resultNOK > 1 ? 'cfm_label_import_error_numbers' : 'cfm_label_import_error_number';
    if (!state.data) return null;
    return state.loading ? /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classes.loadingLoader
    }, _ref2)) : /*#__PURE__*/React.createElement(Fragment, null, okAdvice && /*#__PURE__*/React.createElement(Advice, {
      className: classes.advice,
      type: "success",
      showIcon: true
    }, t('cfm_label_summary_imported_success', [state.data.resultOK, state.data.resultOK + state.data.resultNOK])), !!state.data.resultWOK && /*#__PURE__*/React.createElement(Advice, {
      className: classes.adviceWarning,
      type: "warning",
      showCollapse: false,
      showIcon: true
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
      bold: true
    }, t(warningLiteral, [state.data.resultWOK]))), /*#__PURE__*/React.createElement("div", {
      className: classes.adviceLiterals
    }, /*#__PURE__*/React.createElement(Text, null, t('cfm_label_import_warning_desc_part1')), /*#__PURE__*/React.createElement(Link, {
      className: classes.linkWarning,
      onClick: () => onDownload(true)
    }, t('cfm_action_download_here')), ' ', /*#__PURE__*/React.createElement(Text, null, t('cfm_label_import_warning_desc_part2')))), !!state.data.resultNOK && /*#__PURE__*/React.createElement(Advice, {
      className: classes.adviceError,
      type: "error",
      showCollapse: false,
      showIcon: true
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
      bold: true
    }, t(errorsLiteral, [state.data.resultNOK]))), /*#__PURE__*/React.createElement("div", {
      className: classes.adviceLiterals
    }, /*#__PURE__*/React.createElement(Text, null, t('cfm_label_import_error_desc_part1')), /*#__PURE__*/React.createElement(Link, {
      className: classes.linkError,
      onClick: () => onDownload()
    }, t('cfm_action_download_here')), ' ', /*#__PURE__*/React.createElement(Text, null, t('cfm_label_import_warning_desc_part2')))), /*#__PURE__*/React.createElement(ErrorList, {
      setErrorMessage: setErrorMessage,
      errors: state.errors,
      warnings: state.warnings
    }));
  }, [state.data, state.loading, state.errors, state.warnings, classes.loadingLoader, classes.advice, classes.adviceWarning, classes.adviceLiterals, classes.linkWarning, classes.adviceError, classes.linkError, t, setErrorMessage, onDownload]);
  const showErrorModal = useMemo(() => {
    var _state$errorData, _state$errorData2;
    const advice = state !== null && state !== void 0 && (_state$errorData = state.errorData) !== null && _state$errorData !== void 0 && _state$errorData.isWarning ? {
      class: classes === null || classes === void 0 ? void 0 : classes.adviceWarning,
      type: 'warning'
    } : {
      class: classes === null || classes === void 0 ? void 0 : classes.adviceError,
      type: 'error'
    };
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Advice, {
      className: advice.class,
      type: advice.type,
      showIcon: true
    }, /*#__PURE__*/React.createElement(Text, {
      type: "bold"
    }, t(state === null || state === void 0 ? void 0 : (_state$errorData2 = state.errorData) === null || _state$errorData2 === void 0 ? void 0 : _state$errorData2.error))));
  }, [state === null || state === void 0 ? void 0 : (_state$errorData3 = state.errorData) === null || _state$errorData3 === void 0 ? void 0 : _state$errorData3.isWarning, state === null || state === void 0 ? void 0 : (_state$errorData4 = state.errorData) === null || _state$errorData4 === void 0 ? void 0 : _state$errorData4.error, classes === null || classes === void 0 ? void 0 : classes.adviceWarning, classes.adviceError, t]);
  const errorTitle = useMemo(() => {
    var _state$errorData5, _state$errorData6, _state$errorData7;
    const title = state !== null && state !== void 0 && (_state$errorData5 = state.errorData) !== null && _state$errorData5 !== void 0 && _state$errorData5.isWarning ? 'cfm_label_summary_warning_detail' : 'cfm_label_summary_error_detail';
    return /*#__PURE__*/React.createElement("div", {
      className: classes === null || classes === void 0 ? void 0 : classes.errorTitle
    }, /*#__PURE__*/React.createElement(Icon, {
      className: classes === null || classes === void 0 ? void 0 : classes.arrowIcon,
      onClick: () => setErrorMessage(false, null),
      name: "arrowBack",
      size: "large"
    }), /*#__PURE__*/React.createElement(Text, {
      type: "h6"
    }, t(title, [state === null || state === void 0 ? void 0 : (_state$errorData6 = state.errorData) === null || _state$errorData6 === void 0 ? void 0 : _state$errorData6.row, state === null || state === void 0 ? void 0 : (_state$errorData7 = state.errorData) === null || _state$errorData7 === void 0 ? void 0 : _state$errorData7.name])));
  }, [setErrorMessage, state, t, classes]);
  return /*#__PURE__*/React.createElement(Modal, _extends({
    title: state.showError ? errorTitle : t('cfm_label_summary')
  }, dataModal, {
    isOpen: state.isOpen,
    onRequestClose: onClose
  }), state.showError ? showErrorModal : listContentModal);
};
process.env.NODE_ENV !== "production" ? ImportedModal.propTypes = {
  onRef: PropTypes.func
} : void 0;
export default /*#__PURE__*/memo(ImportedModal);